// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { dismissTriageItem } from '@/items/api/triage-dismiss-item';
import { ItemTriage } from '@/items/api/triage-types';
import { TriageExternalSystemIcon } from '@/items/components/triage-external-system-icon';
import { itemTriageQueryKey } from '@/items/triage-query-keys';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface TriageDismissDialogProps {
  isOpen: boolean;
  item: ItemTriage | null;
  onClose: () => void;
  onSuccess?: (item: ItemTriage) => void;
}

/**
 * Confirmation dialog for dismissing a triage item.
 * Displays the item's SKU and name, and asks for confirmation before dismissing.
 */
export function TriageDismissDialog(props: TriageDismissDialogProps) {
  const { isOpen, item, onClose, onSuccess } = props;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const dismissMutation = useMutation({
    mutationFn: () => {
      if (!item) {
        throw new Error('No item selected');
      }
      return dismissTriageItem(item.key);
    },
    onSuccess: (updatedItem) => {
      queryClient.invalidateQueries({ queryKey: itemTriageQueryKey });
      setErrorMessage('');
      onClose();
      onSuccess?.(updatedItem);

      showToast({
        severity: 'success',
        element: (
          <span>SKU &quot;{item?.sku || item?.itemName}&quot; dismissed</span>
        ),
      });
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
      setErrorMessage(curated);
    },
  });

  const handleConfirm = () => {
    setErrorMessage('');
    dismissMutation.mutate();
  };

  const handleClose = () => {
    if (dismissMutation.isPending) {
      return;
    }
    setErrorMessage('');
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (dismissMutation.isPending) {
        return;
      }
    }
    handleClose();
  };

  if (!item) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dismiss-sku-dialog-title"
    >
      <DialogTitle
        id="dismiss-sku-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Dismiss SKU
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Remove this item from the review queue
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={dismissMutation.isPending}
          aria-label="Close"
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Item info card */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <TriageExternalSystemIcon
              system={item.externalSystem}
              size="large"
            />
            <Stack direction="column">
              <Stack gap={1} direction="row">
                <Typography variant="subtitle2" sx={{ minWidth: 48 }}>
                  SKU:
                </Typography>
                <Typography variant="body2">{item.sku || 'No SKU'}</Typography>
              </Stack>
              <Stack gap={1} direction="row">
                <Typography variant="subtitle2" sx={{ minWidth: 48 }}>
                  Name:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.itemName}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Warning message */}
          <Typography variant="body2" color="text.secondary">
            This SKU will be moved to the "dismissed" tab. You can classify it
            later if needed.
          </Typography>

          {/* Error message */}
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          size="small"
          variant="text"
          onClick={handleClose}
          disabled={dismissMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleConfirm}
          disabled={dismissMutation.isPending}
        >
          {dismissMutation.isPending ? 'Dismissing...' : 'Dismiss'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
